import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import { courierKeys, useTranslation } from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';
import { revokeCourierPortalLink } from '@dukanti/shared/services';
import { supabase } from '@/lib/supabase';

type CourierPortalLinkActionResult = Awaited<ReturnType<typeof revokeCourierPortalLink>>;

function mapPortalLinkResult(result: CourierPortalLinkActionResult): TranslationKey {
  switch (result.error_code) {
    case 'COURIER_INACTIVE':
      return 'admin.couriers.portal.errors.inactiveCourier' as TranslationKey;
    case 'COURIER_NOT_VERIFIED':
      return 'admin.couriers.portal.errors.unverifiedCourier' as TranslationKey;
    case 'LINK_NOT_FOUND':
      return 'admin.couriers.portal.errors.linkNotFound' as TranslationKey;
    case 'UNAUTHORIZED':
      return 'admin.couriers.errors.permissionDenied' as TranslationKey;
    default:
      return 'admin.couriers.portal.errors.networkFailed' as TranslationKey;
  }
}

export function useRevokeCourierPortalLink(
  storeId: string,
): UseMutationResult<CourierPortalLinkActionResult, unknown, string> {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<CourierPortalLinkActionResult, unknown, string>({
    mutationFn: (courierId) => revokeCourierPortalLink(supabase, { storeId, courierId }),
    onSuccess: (result) => {
      if (!result.success) {
        toast.error(t(mapPortalLinkResult(result)));
        return;
      }
      void queryClient.invalidateQueries({ queryKey: courierKeys.list(storeId) });
      void queryClient.invalidateQueries({
        queryKey: courierKeys.portalLink(storeId, result.courier_id),
      });
      toast.success(t('admin.couriers.portal.revoked' as TranslationKey));
    },
    onError: () => {
      toast.error(t('admin.couriers.portal.errors.networkFailed' as TranslationKey));
    },
  });
}
